import {PointerEvent as ReactPointerEvent, useEffect, useMemo, useRef, useState} from 'react'
import {ArrowDown, ArrowUp, ArrowUpDown, RotateCcw} from 'lucide-react'
import {ReportType} from '../../../types/report-type'
import {ReportDataset} from './reportData'
import {formatReportValue} from './reportFormatting'
import {
  clampColumnWidth,
  fitColumnWidth,
  initialColumnWidth,
  MAX_COLUMN_WIDTH,
  MIN_COLUMN_WIDTH,
  ReportColumn,
  reportColumns,
  ReportSort,
  sortReportRows,
} from './reportTableModel'
import {TableFrame} from './ReportTable.styles'

type Props = {type: ReportType; title: string; dataset: ReportDataset; viewId: string}
type Widths = Record<string, number>
function readWidths(key: string): Widths {
  try {
    const stored = JSON.parse(localStorage.getItem(key) || '{}')
    if (!stored || typeof stored !== 'object' || Array.isArray(stored)) return {}
    return Object.fromEntries(
      Object.entries(stored)
        .filter(([, width]) => typeof width === 'number' && Number.isFinite(width))
        .map(([column, width]) => [column, clampColumnWidth(width as number)])
    )
  } catch {
    return {}
  }
}

export default function ReportTable({type, title, dataset, viewId}: Props) {
  const storageKey = `report-column-widths:v1:${type}:${viewId}`
  const [widths, setWidths] = useState<Widths>(() => readWidths(storageKey))
  const [sort, setSort] = useState<ReportSort>(null)
  const columns = useMemo(() => reportColumns(type, dataset.columns, dataset.rows), [type, dataset])
  const rows = useMemo(
    () => sortReportRows(dataset.rows, columns, sort),
    [dataset.rows, columns, sort]
  )
  const table = useRef<HTMLTableElement>(null)
  const cols = useRef<(HTMLTableColElement | null)[]>([])
  const dragCleanup = useRef<(() => void) | null>(null)
  const widthOf = (column: ReportColumn) => widths[column.id] ?? initialColumnWidth(column)
  const totalWidth = columns.reduce((total, column) => total + widthOf(column), 0)

  useEffect(() => {
    try {
      localStorage.setItem(storageKey, JSON.stringify(widths))
    } catch {}
  }, [storageKey, widths])
  useEffect(
    () => () => {
      dragCleanup.current?.()
    },
    []
  )

  const measuredWidths = (): Widths =>
    Object.fromEntries(
      columns.map((column) => [
        column.id,
        table.current?.tHead?.rows[0]?.cells[column.index]?.getBoundingClientRect().width ||
          widthOf(column),
      ])
    )
  const resize = (column: ReportColumn, width: number) =>
    setWidths({...measuredWidths(), [column.id]: clampColumnWidth(width)})
  const startResize = (event: ReactPointerEvent<HTMLDivElement>, column: ReportColumn) => {
    if (event.button !== 0) return
    event.preventDefault()
    event.stopPropagation()
    dragCleanup.current?.()
    const frozen = measuredWidths()
    const startWidth = frozen[column.id],
      startX = event.clientX
    const total = Object.values(frozen).reduce((sum, width) => sum + width, 0)
    let latest = startWidth
    const handle = event.currentTarget
    const previousCursor = document.body.style.cursor,
      previousSelect = document.body.style.userSelect
    document.body.style.cursor = 'col-resize'
    document.body.style.userSelect = 'none'
    handle.classList.add('is-resizing')
    columns.forEach((item) => {
      if (cols.current[item.index]) cols.current[item.index]!.style.width = `${frozen[item.id]}px`
    })
    if (table.current) {
      table.current.style.width = `${total}px`
      table.current.style.minWidth = '0'
    }
    const move = (pointer: PointerEvent) => {
      if (!Number.isFinite(pointer.clientX)) return
      latest = clampColumnWidth(startWidth + pointer.clientX - startX)
      // Resize only the colgroup while dragging; thousands of cells stay untouched.
      if (cols.current[column.index]) cols.current[column.index]!.style.width = `${latest}px`
      if (table.current) table.current.style.width = `${total + latest - startWidth}px`
      handle.setAttribute('aria-valuenow', String(latest))
    }
    const cleanup = () => {
      window.removeEventListener('pointermove', move)
      window.removeEventListener('pointerup', finish)
      window.removeEventListener('pointercancel', finish)
      window.removeEventListener('blur', finish)
      document.body.style.cursor = previousCursor
      document.body.style.userSelect = previousSelect
      handle.classList.remove('is-resizing')
      dragCleanup.current = null
    }
    const finish = () => {
      cleanup()
      setWidths({...frozen, [column.id]: latest})
    }
    dragCleanup.current = cleanup
    window.addEventListener('pointermove', move)
    window.addEventListener('pointerup', finish)
    window.addEventListener('pointercancel', finish)
    window.addEventListener('blur', finish)
  }
  const toggleSort = (column: ReportColumn) =>
    setSort((current) =>
      current?.columnId !== column.id
        ? {columnId: column.id, direction: 'ascending'}
        : current.direction === 'ascending'
        ? {columnId: column.id, direction: 'descending'}
        : null
    )
  const body = useMemo(
    () =>
      rows.map((row, index) => (
        <tr key={index}>
          {columns.map((column) => {
            const value = formatReportValue(type, column.key, row[column.index])
            return (
              <td
                key={column.id}
                className={column.kind === 'number' ? 'number-cell' : undefined}
                title={value}
              >
                {value}
              </td>
            )
          })}
        </tr>
      )),
    [rows, columns, type]
  )

  return (
    <TableFrame>
      <div
        className='report-table-scroll'
        role='region'
        aria-label={`Tabela de ${title}`}
        tabIndex={0}
      >
        <table
          ref={table}
          className={Object.keys(widths).length ? 'has-custom-widths' : ''}
          style={{width: totalWidth}}
        >
          <caption className='visually-hidden'>{title}</caption>
          <colgroup>
            {columns.map((column) => (
              <col
                key={column.id}
                ref={(element) => {
                  cols.current[column.index] = element
                }}
                style={{width: widthOf(column)}}
              />
            ))}
          </colgroup>
          <thead>
            <tr>
              {columns.map((column) => {
                const direction = sort?.columnId === column.id ? sort.direction : 'none'
                const SortIcon =
                  direction === 'ascending'
                    ? ArrowUp
                    : direction === 'descending'
                    ? ArrowDown
                    : ArrowUpDown
                return (
                  <th key={column.id} scope='col' aria-label={column.label} aria-sort={direction}>
                    <button
                      type='button'
                      className='column-sort'
                      aria-label={`Ordenar por ${column.label}`}
                      title={`Ordenar por ${column.label}`}
                      onClick={() => toggleSort(column)}
                    >
                      <span>{column.label}</span>
                      <SortIcon size={14} aria-hidden='true' />
                    </button>
                    <div
                      role='separator'
                      aria-label={`Ajustar largura de ${column.label}`}
                      aria-orientation='vertical'
                      tabIndex={0}
                      aria-valuemin={MIN_COLUMN_WIDTH}
                      aria-valuemax={MAX_COLUMN_WIDTH}
                      aria-valuenow={widthOf(column)}
                      className='column-resizer'
                      onPointerDown={(event) => startResize(event, column)}
                      onDoubleClick={(event) => {
                        event.stopPropagation()
                        resize(column, fitColumnWidth(type, column, dataset.rows))
                      }}
                      onKeyDown={(event) => {
                        if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return
                        event.preventDefault()
                        event.stopPropagation()
                        resize(
                          column,
                          measuredWidths()[column.id] + (event.key === 'ArrowRight' ? 16 : -16)
                        )
                      }}
                      title='Arraste para ajustar. Clique duas vezes para ajustar ao conteúdo.'
                    />
                  </th>
                )
              })}
            </tr>
          </thead>
          <tbody>{body}</tbody>
        </table>
      </div>
      <div className='table-notes'>
        <p>
          Ordenação nos resultados exibidos. Arraste a divisória do cabeçalho para ajustar as
          colunas.
        </p>
        <button
          type='button'
          className='btn btn-sm btn-light'
          disabled={!Object.keys(widths).length}
          onClick={() => {
            if (table.current) table.current.style.minWidth = ''
            setWidths({})
          }}
        >
          <RotateCcw size={13} aria-hidden='true' /> Restaurar larguras
        </button>
      </div>
    </TableFrame>
  )
}
